/**
 * Browser-side AES-256-GCM, wire-compatible with the orchestrator.
 *
 *     <iv hex>:<ciphertext hex>:<auth tag hex>
 *
 * Switched on at BUILD time by `VITE_API_ENCRYPT`, with the key in
 * `VITE_API_ENCRYPTION_KEY`. Both are baked into the bundle by Vite, which is
 * the whole caveat:
 *
 * ── READ THIS BEFORE ENABLING IT ANYWHERE REAL ──────────────────────────────
 * A browser cannot keep a secret. `VITE_*` values are substituted into the
 * JavaScript at build time, so with encryption on, the key is sitting in a
 * file the server hands to anyone who asks for the page. Anyone who opens
 * devtools can read it, and with it decrypt every request and response.
 *
 * That makes this an ENCODING, not a protection. It is genuinely useful for
 * exercising the encrypted path end to end -- which is what it was added for --
 * and it is not a security control. The traffic is actually protected by being
 * same-origin, by the HttpOnly session cookie, and by TLS at the gateway.
 *
 * Default is OFF. Turn it on to test; turn it off before this is in front of
 * customers.
 * ───────────────────────────────────────────────────────────────────────────
 */

const RAW_KEY = (import.meta.env.VITE_API_ENCRYPTION_KEY ?? "") as string;

/** Whether this build encrypts. Off unless explicitly switched on via VITE_API_ENCRYPT=true. */
export const API_ENCRYPT: boolean =
  String(import.meta.env.VITE_API_ENCRYPT ?? "false").toLowerCase() === "true";

/**
 * `crypto.subtle` exists only in a secure context: HTTPS, or localhost.
 * On a plain-HTTP host that is not localhost it is `undefined`, and the
 * failure would otherwise be a confusing "cannot read property importKey of
 * undefined" on the first request rather than a statement of the cause.
 */
const SUBTLE_AVAILABLE =
  typeof crypto !== "undefined" && typeof crypto.subtle !== "undefined";

if (API_ENCRYPT && !SUBTLE_AVAILABLE) {
  console.error(
    "VITE_API_ENCRYPT is true but crypto.subtle is unavailable. The Web Crypto " +
      "API requires a secure context — serve over HTTPS, or use localhost."
  );
}

if (API_ENCRYPT && new TextEncoder().encode(RAW_KEY).length !== 32) {
  console.error(
    `VITE_API_ENCRYPTION_KEY must be exactly 32 bytes for aes-256-gcm; got ` +
      `${new TextEncoder().encode(RAW_KEY).length}. It is used as raw UTF-8 bytes, ` +
      "matching the orchestrator and the Node services."
  );
}

let keyPromise: Promise<CryptoKey> | null = null;

function getKey(): Promise<CryptoKey> {
  // Imported once and reused: importKey is async, and doing it per request
  // would add a round of work to every call for no benefit.
  if (!keyPromise) {
    keyPromise = crypto.subtle.importKey(
      "raw",
      // Raw UTF-8 bytes of the configured string — NOT hex- or base64-decoded.
      // Decoding here would yield a different key from the same value and
      // nothing would ever decrypt.
      new TextEncoder().encode(RAW_KEY),
      { name: "AES-GCM" },
      false,
      ["encrypt", "decrypt"]
    );
  }
  return keyPromise;
}

const toHex = (buf: ArrayBuffer | Uint8Array): string =>
  Array.from(buf instanceof Uint8Array ? buf : new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const fromHex = (hex: string): Uint8Array => {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
};

/** `<iv>:<ciphertext>:<tag>`, hex. */
export async function encryptPayload(plaintext: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const sealed = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      await getKey(),
      new TextEncoder().encode(plaintext)
    )
  );
  // WebCrypto returns ciphertext||tag in one buffer; the wire format keeps the
  // 16-byte tag as its own field, as Node's getAuthTag() produces.
  return [
    toHex(iv),
    toHex(sealed.slice(0, -16)),
    toHex(sealed.slice(-16)),
  ].join(":");
}

/** Reverse of `encryptPayload`. */
export async function decryptPayload(payload: string): Promise<string> {
  const parts = payload.split(":");
  if (parts.length !== 3 || parts.some((p) => !p)) {
    throw new Error("Invalid encryption data format");
  }
  const [iv, ciphertext, tag] = parts.map(fromHex);

  // Re-join ciphertext and tag, which is the shape WebCrypto expects.
  const sealed = new Uint8Array(ciphertext.length + tag.length);
  sealed.set(ciphertext);
  sealed.set(tag, ciphertext.length);

  try {
    const opened = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      await getKey(),
      sealed
    );
    return new TextDecoder().decode(opened);
  } catch {
    // GCM authenticates as well as encrypts, so a failure here means the
    // payload was altered in transit or encrypted under a different key.
    throw new Error(
      "Could not decrypt the response: it was modified in transit, or the " +
        "server is using a different key than this build."
    );
  }
}

/**
 * Unwrap a response body that may or may not be encrypted.
 *
 * The server leaves errors and a few endpoints plain even when encryption is
 * on, so this decides per response rather than per build: a body of exactly
 * `{ data: "<iv>:<ct>:<tag>" }` is ciphertext; anything else is already the
 * answer.
 */
export async function unwrapResponse<T>(body: unknown): Promise<T> {
  if (!API_ENCRYPT) return body as T;

  if (
    body &&
    typeof body === "object" &&
    !Array.isArray(body) &&
    Object.keys(body as object).length === 1 &&
    typeof (body as { data?: unknown }).data === "string"
  ) {
    const cipher = (body as { data: string }).data;
    // Three colon-separated hex fields — the shape check that tells ciphertext
    // apart from a legitimate `data` string field.
    if (/^[0-9a-f]+:[0-9a-f]+:[0-9a-f]+$/i.test(cipher)) {
      return JSON.parse(await decryptPayload(cipher)) as T;
    }
  }
  return body as T;
}

/** Wrap a request body as `{ data: "<ciphertext>" }` when encryption is on. */
export async function wrapRequest(payload: unknown): Promise<string> {
  const json = JSON.stringify(payload);
  if (!API_ENCRYPT) return json;
  return JSON.stringify({ data: await encryptPayload(json) });
}
